/**
 * Complete chat box component combining transcript and input.
 * Provides a ready-to-use chat interface with MCP tool indicators
 * and optional MCP configuration.
 */
import { ChatTranscript, type PendingInteractiveBlock } from './ChatTranscript';
import { ChatInput } from './ChatInput';
import type { ChatSuggestion } from './ChatInput';
import type { ChatInputHandle } from './ChatInput';
import { PendingBlockArea } from './PendingBlockArea';
import { ToolModulesIndicator } from './ToolModulesIndicator';
import { usePendingBlockDismiss } from './usePendingBlockDismiss';
import type { ChatMessage, StreamingState, McpConfig } from './types';

interface ChatBoxProps {
  /** Conversation messages */
  messages: ChatMessage[];
  /** Called when user sends a message with optional file IDs */
  onSend: (text: string, fileIds?: string[]) => void;
  /** Called when user clicks stop button to interrupt generation */
  onStop?: () => void;
  /** Streaming state for real-time updates */
  streaming?: StreamingState;
  /** Disable input (e.g., while connecting or sending) */
  disabled?: boolean;
  /** Height of the transcript container */
  height?: number | string;
  /** Empty state message for transcript */
  emptyMessage?: string;
  /** Custom empty state content (takes priority over emptyMessage) */
  emptyStateContent?: React.ReactNode;
  /** Placeholder text for input */
  inputPlaceholder?: string;
  /** Whether to auto-focus input on mount */
  autoFocus?: boolean;
  /** Additional content to render below the transcript (e.g., avatar panel) */
  children?: React.ReactNode;
  /** MCP modules enabled for this chat session */
  enabledModules?: string[];
  /** Session ID for tool call detail fetching */
  sessionId?: string;
  /** MCP configuration for this chat (if configurable) */
  mcpConfig?: McpConfig;
  /** Called when MCP configuration changes */
  onMcpConfigChange?: (config: McpConfig) => void;
  /** Whether to show the MCP configure button */
  showMcpConfig?: boolean;
  /** Optional model selector rendered below the input */
  modelSelector?: React.ReactNode;
  /** Accepted MIME types for file attachments */
  acceptedMimeTypes?: string[];
  /** Called when the current pending block changes (for rendering in input area) */
  onPendingBlockUpdate?: (pending: PendingInteractiveBlock | null) => void;
  /** Called when user clicks a created entity link (e.g., to close a panel) */
  onNavigate?: () => void;
  /** Extra content rendered on the left side of the input toolbar (e.g., asset picker) */
  extraToolbarLeft?: React.ReactNode;
  /** Pre-populate input text (e.g., from quick actions) */
  initialValue?: string;
  /** Called when initialValue has been applied to the input */
  onInitialValueConsumed?: () => void;
  /** Clickable suggestions shown inside the input when empty */
  suggestions?: ChatSuggestion[];
  /** Optional: collapse the inline pending banner immediately when user clicks
   *  approve/reject/always so it doesn't flash back while the backend executes. */
  onApprovalResolveStart?: (toolCallId: string, action: 'approve' | 'reject' | 'always_approve') => void;
  /** Ref to imperatively focus the chat input */
  inputRef?: React.Ref<ChatInputHandle>;
}

export const ChatBox: React.FC<ChatBoxProps> = ({
  messages,
  onSend,
  onStop,
  streaming,
  disabled = false,
  height = 300,
  emptyMessage,
  emptyStateContent,
  inputPlaceholder,
  autoFocus = false,
  children,
  enabledModules = [],
  sessionId,
  mcpConfig,
  onMcpConfigChange,
  showMcpConfig = false,
  modelSelector,
  acceptedMimeTypes,
  onPendingBlockUpdate,
  onNavigate,
  extraToolbarLeft,
  initialValue,
  onInitialValueConsumed,
  suggestions,
  onApprovalResolveStart,
  inputRef,
}) => {
  const { pendingBlock, handlePendingBlockUpdate, handleDismissPendingBlock } =
    usePendingBlockDismiss({ onPendingBlockUpdate });

  // Build toolbar content for the unified input container
  const mcpIndicator = (enabledModules.length > 0 || sessionId) ? (
    <ToolModulesIndicator
      enabledModules={enabledModules}
      toolEvents={streaming?.toolEvents}
      activeToolName={streaming?.activeToolName}
      showConfigButton={showMcpConfig}
      mcpConfig={mcpConfig}
      onMcpConfigChange={onMcpConfigChange}
      sessionId={sessionId}
    />
  ) : undefined;

  const toolbarLeftContent = (mcpIndicator || extraToolbarLeft) ? (
    <>
      {extraToolbarLeft}
      {mcpIndicator}
    </>
  ) : undefined;

  const isFlexHeight = height === '100%';

  return (
    <div className={`flex flex-col ${isFlexHeight ? 'flex-1 min-h-0' : 'h-full'}`}>
      <div className={isFlexHeight ? 'flex-1 min-h-0' : undefined}>
        <ChatTranscript
          messages={messages}
          streaming={streaming}
          height={height}
          emptyMessage={emptyMessage}
          emptyStateContent={emptyStateContent}
          sessionId={sessionId}
          onSend={onSend}
          onPendingBlockUpdate={handlePendingBlockUpdate}
          onNavigate={onNavigate}
          onApprovalResolveStart={onApprovalResolveStart}
        />
      </div>
      {children}

      <ChatInput
        ref={inputRef}
        onSend={onSend}
        onStop={onStop}
        disabled={disabled}
        isGenerating={streaming?.isGenerating}
        placeholder={inputPlaceholder}
        autoFocus={autoFocus}
        focusSignal={sessionId ?? null}
        initialValue={initialValue}
        onInitialValueConsumed={onInitialValueConsumed}
        acceptedMimeTypes={acceptedMimeTypes}
        toolbarLeft={toolbarLeftContent}
        suggestions={suggestions}
        toolbarRight={modelSelector}
        replacementContent={
          pendingBlock ? (
            <PendingBlockArea
              pendingBlock={pendingBlock}
              onDismiss={handleDismissPendingBlock}
              className=""
            />
          ) : undefined
        }
      />
    </div>
  );
};
